import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getProductoById, createProducto, updateProducto, deleteProducto, getCompanias } from "../../services/divinaProductsService";
import { useSupabaseClient } from "../../lib/supabase";
import { Button } from "../../components/ui/button";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { Alert, AlertDescription } from "../../components/ui/alert";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import SectionPreview from "../../components/product/SectionPreview.jsx";
import {
  FK, PageHead, FormCard, Section, Row, Field, TextInput, TextArea, Select,
  ActionBar, EstadoRow, Pill, FkPrimary, FkGhost, FkDanger, YIcon,
} from "../../components/product_config/formkit";

export default function DivinaProductsForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = Boolean(id);
  const supabaseClient = useSupabaseClient();

  const [formData, setFormData] = useState({
    nombre: "",
    descripcion: "",
    descripcion_json: "",
    compania_id: "",
    imagen_url: "",
    imagen_hero_url: "",
    yoolbrand: "",
    yoolbrand_slug: "",
    ramo: "",
    yooladmin_subscription_plan_id: "",
    productDescription: "",
    productSchema: "",
    activo: true,
    archivado: false
  });

  const [companias, setCompanias] = useState([]);
  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [productDescriptionObj, setProductDescriptionObj] = useState(null);

  // Load data if editing
  useEffect(() => {
    if (!supabaseClient) return;
    const loadData = async () => {
      try {
        // Load companias
        const companiasData = await getCompanias(supabaseClient);
        setCompanias(companiasData);

        // Load producto if editing
        if (isEditing) {
          setLoading(true);
          const data = await getProductoById(id);
          setFormData({
            nombre: data.nombre || "",
            descripcion: data.descripcion || "",
            descripcion_json: data.descripcion_json ? JSON.stringify(data.descripcion_json, null, 2) : "",
            compania_id: data.compania_id || "",
            imagen_url: data.imagen_url || "",
            imagen_hero_url: data.imagen_hero_url || "",
            yoolbrand: data.yoolbrand || "",
            yoolbrand_slug: data.yoolbrand_slug || "",
            ramo: data.ramo || "",
            yooladmin_subscription_plan_id: data.yooladmin_subscription_plan_id || "",
            productDescription: data["product-description"] ? JSON.stringify(data["product-description"], null, 2) : "",
            productSchema: data.product_description_schemma ? JSON.stringify(data.product_description_schemma, null, 2) : "",
            activo: data.activo !== false,
            archivado: data.archivado === true
          });
          try {
            const obj = data["product-description"] || null;
            setProductDescriptionObj(obj || null);
          } catch {
            setProductDescriptionObj(null);
          }
        }
      } catch (err) {
        console.error("Error loading data:", err);
        setError("No se pudo cargar la información");
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [id, isEditing, supabaseClient]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;

    setFormData(prev => ({
      ...prev,
      [name]: newValue
    }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle archive toggle
  const handleArchiveToggle = async (newArchivedState) => {
    try {
      setLoading(true);
      await updateProducto(id, {
        ...formData,
        archivado: newArchivedState
      });

      setFormData(prev => ({
        ...prev,
        archivado: newArchivedState
      }));

      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      console.error("Error updating archive status:", err);
      setError("Error al actualizar el estado de archivo");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    try {
      // Parse JSON if provided
      let descripcionJson = null;
      if (formData.descripcion_json.trim()) {
        try {
          descripcionJson = JSON.parse(formData.descripcion_json);
        } catch {
          setError("El JSON de descripción no es válido");
          setSaving(false);
          return;
        }
      }

      // Parse product description JSON
      let productDescriptionJson = null;
      if (formData.productDescription.trim()) {
        try {
          productDescriptionJson = JSON.parse(formData.productDescription);
        } catch {
          setError("El JSON de secciones (product-description) no es válido");
          setSaving(false);
          return;
        }
      }

      // Parse product schema JSON
      let productSchemaJson = null;
      if (formData.productSchema.trim()) {
        try {
          productSchemaJson = JSON.parse(formData.productSchema);
        } catch {
          setError("El JSON de esquema (product_description_schemma) no es válido");
          setSaving(false);
          return;
        }
      }

      const dataToSubmit = {
        ...formData,
        descripcion_json: descripcionJson,
        imagen_url: formData.imagen_url || null,
        imagen_hero_url: formData.imagen_hero_url || null,
        yoolbrand_slug: formData.yoolbrand_slug || null,
        ramo: formData.ramo || null,
        yooladmin_subscription_plan_id: formData.yooladmin_subscription_plan_id || null,
        "product-description": productDescriptionJson,
        product_description_schemma: productSchemaJson,
        compania_id: formData.compania_id || null
      };

      if (isEditing) {
        await updateProducto(id, dataToSubmit);
      } else {
        await createProducto(dataToSubmit);
      }

      setSuccess(true);
      if (!isEditing) {
        setTimeout(() => {
          navigate('/product_config/productos');
        }, 1500);
      }
    } catch (err) {
      console.error("Error saving producto:", err);
      setError(err.message || "Error al guardar el producto");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await deleteProducto(id);
      navigate('/product_config/productos');
    } catch (err) {
      console.error("Error deleting producto:", err);
      setError("Error al eliminar el producto");
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  const companiaOptions = companias.map((c) => ({ value: c.id.toString(), label: c.nombre_corto }));

  return (
    <div className="p-6" style={{ maxWidth: 980, margin: "0 auto" }}>
      <PageHead
        title={<><span style={{ color: FK.text }}>{isEditing ? "Editar" : "Nuevo"}</span> <span style={{ color: FK.blue }}>producto</span></>}
        sub={isEditing ? "Modifica la información del producto" : "Crea un nuevo producto de Divina"}
        onBack={() => navigate('/product_config/productos')}
      />

      {error && (
        <Alert className="mb-6 border-red-200 bg-red-50">
          <AlertDescription className="text-red-800">{error}</AlertDescription>
        </Alert>
      )}
      {success && (
        <Alert className="mb-6 border-green-200 bg-green-50">
          <AlertDescription className="text-green-800">
            Producto {isEditing ? 'actualizado' : 'creado'} exitosamente
          </AlertDescription>
        </Alert>
      )}

      <FormCard>
        <form onSubmit={handleSubmit}>
          {/* ── Información básica ── */}
          <Section title="Información básica" first>
            <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
              <Row>
                <Field label="Nombre del producto" required htmlFor="nombre">
                  <TextInput id="nombre" name="nombre" value={formData.nombre} onChange={handleChange} placeholder="Ingresa el nombre del producto" />
                </Field>
                <Field label="Compañía" htmlFor="compania_id">
                  <Select
                    id="compania_id"
                    value={formData.compania_id ? formData.compania_id.toString() : ""}
                    placeholder="Selecciona una compañía"
                    options={companiaOptions}
                    onChange={(e) => handleSelectChange('compania_id', e.target.value)}
                  />
                </Field>
              </Row>

              <Row>
                <Field label="Marca (yoolbrand)" htmlFor="yoolbrand">
                  <TextInput id="yoolbrand" name="yoolbrand" value={formData.yoolbrand} onChange={handleChange} placeholder="Ej: yool" />
                </Field>
                <Field label="Brand slug (yoolbrand_slug)" htmlFor="yoolbrand_slug">
                  <TextInput id="yoolbrand_slug" name="yoolbrand_slug" value={formData.yoolbrand_slug} onChange={handleChange} placeholder="Ej: yool-seguros" />
                </Field>
              </Row>

              <Row>
                <Field label="Ramo" htmlFor="ramo">
                  <TextInput id="ramo" name="ramo" value={formData.ramo} onChange={handleChange} placeholder="Ej: auto, vida, salud" />
                </Field>
                <Field label="Plan de suscripción (yooladmin)" htmlFor="yooladmin_subscription_plan_id">
                  <TextInput id="yooladmin_subscription_plan_id" name="yooladmin_subscription_plan_id" value={formData.yooladmin_subscription_plan_id} onChange={handleChange} placeholder="ID del plan en yooladmin" />
                </Field>
              </Row>

              <Field label="Descripción" htmlFor="descripcion" full>
                <TextArea id="descripcion" name="descripcion" value={formData.descripcion} onChange={handleChange} rows={3} placeholder="Describe el producto..." />
              </Field>
            </div>
          </Section>

          {/* ── Configuración de presentación ── */}
          <Section title="Configuración de presentación" desc="Cómo se muestra el producto en el cotizador público.">
            <Tabs defaultValue="hero">
              <TabsList>
                <TabsTrigger value="hero">Hero</TabsTrigger>
                <TabsTrigger value="metadata">Metadatos</TabsTrigger>
                <TabsTrigger value="sections">Secciones</TabsTrigger>
              </TabsList>

              <TabsContent value="hero" className="space-y-4">
                <Row>
                  <Field label="Imagen Hero URL" htmlFor="imagen_hero_url">
                    <TextInput id="imagen_hero_url" name="imagen_hero_url" value={formData.imagen_hero_url} onChange={handleChange} placeholder="https://.../hero.jpg" />
                  </Field>
                  <Field label="Imagen Principal URL" htmlFor="imagen_url">
                    <TextInput id="imagen_url" name="imagen_url" value={formData.imagen_url} onChange={handleChange} placeholder="https://.../imagen.jpg" />
                  </Field>
                </Row>
                <div style={{ border: "1px solid " + FK.border, borderRadius: 12, padding: 16 }}>
                  <h4 className="text-md font-semibold mb-2">Previsualización</h4>
                  {(() => {
                    let heroData = null;
                    try {
                      heroData = formData.productDescription?.trim() ? JSON.parse(formData.productDescription).hero : null;
                    } catch {
                      heroData = null;
                    }
                    if (!heroData) {
                      return (
                        <div className="text-sm text-muted-foreground">
                          Agrega contenido de hero en el JSON para visualizar.
                        </div>
                      );
                    }
                    return (
                      <div className="text-center">
                        <div className="fancyyool-title">{heroData?.title}</div>
                        <div className="fancyyool-inline-subtitle">{heroData?.subtitle}</div>
                        <p className="mt-2 text-muted-foreground">{heroData?.description}</p>
                        <div className="mt-4 flex justify-center gap-2">
                          <Button variant="outline">{heroData?.cta?.primary}</Button>
                          <Button variant="outline">{heroData?.cta?.secondary}</Button>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </TabsContent>

              <TabsContent value="metadata" className="space-y-4">
                <Tabs defaultValue="descripcion_json" className="w-full">
                  <TabsList className="grid w-full grid-cols-3">
                    <TabsTrigger value="descripcion_json">Descripción JSON</TabsTrigger>
                    <TabsTrigger value="productDescription">Product Description</TabsTrigger>
                    <TabsTrigger value="productSchema">Schema</TabsTrigger>
                  </TabsList>

                  <TabsContent value="descripcion_json" className="mt-4">
                    <div>
                      <Label htmlFor="descripcion_json">Descripción JSON (general)</Label>
                      <Textarea
                        id="descripcion_json"
                        name="descripcion_json"
                        value={formData.descripcion_json}
                        onChange={handleChange}
                        rows={16}
                        placeholder='{ "notas": "valor" }'
                        className="font-mono text-sm"
                      />
                    </div>
                  </TabsContent>

                  <TabsContent value="productDescription" className="mt-4">
                    <div>
                      <Label htmlFor="productDescription">Product Description (sections JSON)</Label>
                      <Textarea
                        id="productDescription"
                        name="productDescription"
                        value={formData.productDescription}
                        onChange={handleChange}
                        rows={16}
                        placeholder='{ "hero": {...}, "sections": [ ... ] }'
                        className="font-mono text-sm"
                      />
                      <p className="text-sm text-gray-500 mt-1">
                        Usa el formato de {"docs/product-description.json"} y aplica clases FancyYool.
                      </p>
                    </div>
                  </TabsContent>

                  <TabsContent value="productSchema" className="mt-4">
                    <div>
                      <Label htmlFor="productSchema">Schema (product_description_schemma)</Label>
                      <Textarea
                        id="productSchema"
                        name="productSchema"
                        value={formData.productSchema}
                        onChange={handleChange}
                        rows={16}
                        placeholder="{ ... }"
                        className="font-mono text-sm"
                      />
                    </div>
                  </TabsContent>
                </Tabs>
              </TabsContent>

              <TabsContent value="sections" className="space-y-4">
                <div style={{ border: "1px solid " + FK.border, borderRadius: 12, padding: 16, overflow: "hidden" }}>
                  {(() => {
                    let parsed = null;
                    try {
                      parsed = productDescriptionObj || (formData.productDescription?.trim() ? JSON.parse(formData.productDescription) : null);
                    } catch {
                      parsed = null;
                    }
                    if (!parsed) {
                      return (
                        <div className="text-sm text-muted-foreground">Sin datos de secciones para previsualizar.</div>
                      );
                    }
                    const handlePreviewChange = (updatedObj) => {
                      setProductDescriptionObj(updatedObj);
                      setFormData(prev => ({
                        ...prev,
                        productDescription: JSON.stringify(updatedObj, null, 2)
                      }));
                    };
                    return <SectionPreview productDescription={parsed} onChange={handlePreviewChange} />;
                  })()}
                </div>
              </TabsContent>
            </Tabs>
          </Section>

          {/* ── Estado ── */}
          <Section title="Estado">
            <div style={{ display: "flex", flexWrap: "wrap", gap: 16 }}>
              <EstadoRow
                titulo="Producto activo"
                texto="Visible en el cotizador y disponible para contratar."
                on={formData.activo}
                onChange={(v) => handleSelectChange('activo', v)}
                pill={<Pill tone={formData.activo ? "ok" : "muted"}>{formData.activo ? "Activo" : "Inactivo"}</Pill>}
              />
              {isEditing && (
                <EstadoRow
                  titulo="Archivado"
                  texto="Se oculta de los listados sin eliminar su historial."
                  on={formData.archivado}
                  onChange={handleArchiveToggle}
                  pill={formData.archivado ? <Pill tone="muted">Archivado</Pill> : null}
                />
              )}
            </div>
          </Section>

          {/* ── Acciones ── */}
          <div style={{ paddingTop: 22, marginTop: 6, borderTop: "1px solid " + FK.line }}>
            <ActionBar
              left={isEditing ? (
                <FkDanger onClick={() => setDeleteDialogOpen(true)}><YIcon name="x" size={16} /> Eliminar producto</FkDanger>
              ) : null}
              right={<>
                <FkGhost onClick={() => navigate('/product_config/productos')}>Cancelar</FkGhost>
                <FkPrimary type="submit" disabled={saving}>{saving ? 'Guardando...' : (isEditing ? 'Actualizar' : 'Crear producto')}</FkPrimary>
              </>}
            />
          </div>
        </form>
      </FormCard>

      {/* Dialog de confirmación para eliminar */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Estás seguro?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. Se eliminará permanentemente el producto
              "{formData.nombre}".
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
